import React, { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import {
  useInput,
  useRecordContext,
} from 'react-admin';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  Calculate as CalculateIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { normalizeCreditSituation } from '../utils/caseDisplayUtils';
import { calculateEndeudamiento, TAE_FORMULARIO } from '../utils/endeudamiento';

// Credit amounts are often stored as Spanish-formatted strings ("156,83",
// "298,75€", "17.452,60"). A type="number" input can't display those, so it
// shows blank/0. Normalize to a plain dot-decimal number string for the input.
const toNumberInputValue = (v) => {
  if (v === null || v === undefined || v === '') return '';
  let s = String(v).replace(/[€\s]/g, '').trim();
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.'); // "17.452,60" -> "17452.60"
  s = s.replace(/[^0-9.-]/g, '');
  return s;
};

const ratioColor = (value) => {
  if (!Number.isFinite(value)) return 'text.primary';
  if (value > 50) return 'error.main';
  if (value > 45) return 'warning.main';
  return 'text.primary';
};

const AdminEditableCreditInformation = ({ source = "creditos" }) => {
  const record = useRecordContext();
  const { field } = useInput({ source });
  const { field: calculatorField } = useInput({ source: 'calculadoraEndeudamiento' });
  const formValues = useWatch();

  // Credits saved on the case.
  const recordCreditos = Array.isArray(record?.[source]) ? record[source] : [];

  // Show the form field's value once it holds credits; otherwise fall back to
  // the record's credits so they are visible while editing.
  const creditos =
    Array.isArray(field.value) && field.value.length ? field.value : recordCreditos;

  const calculator = {
    honorarios: '',
    plazoMeses: 120,
    ...(record?.calculadoraEndeudamiento || {}),
    ...(calculatorField.value || {}),
    tasaAnual: TAE_FORMULARIO,
  };
  const currentRecord = { ...(record || {}), ...(formValues || {}) };
  const calculo = useMemo(() => calculateEndeudamiento({
    creditos,
    primerTitular: currentRecord.primerTitular,
    segundoTitular: currentRecord.segundoTitular,
    gastosVivienda: currentRecord.gastosVivienda,
    otrosDatos: currentRecord.otrosDatos,
    honorarios: calculator.honorarios,
    plazoMeses: calculator.plazoMeses,
    tasaAnual: calculator.tasaAnual,
    incluirSeguro: !!calculator.incluirSeguro,
  }), [creditos, currentRecord.primerTitular, currentRecord.segundoTitular, currentRecord.gastosVivienda, currentRecord.otrosDatos, calculator.honorarios, calculator.plazoMeses, calculator.tasaAnual, calculator.incluirSeguro]);

  const updateCalculator = (name, value) => {
    calculatorField.onChange({ ...calculator, [name]: value });
  };

  const saveOfferSnapshot = () => {
    calculatorField.onChange({
      ...calculator,
      honorarios: calculo.honorarios,
      ultimaOferta: {
        ...calculo,
        guardadoEn: new Date().toISOString(),
      },
    });
  };

  const formatCalculatedCurrency = (value) => (
    Number.isFinite(value)
      ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value)
      : '—'
  );

  const formatCalculatedPercentage = (value) => (
    Number.isFinite(value)
      ? `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 }).format(value)} %`
      : '—'
  );

  // Register the record's credits into the form on mount when the field is
  // empty, so they are both visible and preserved on save (useInput can leave
  // an array-of-objects unpopulated).
  const didInit = useRef(false);
  useEffect(() => {
    if (
      !didInit.current &&
      recordCreditos.length &&
      (!Array.isArray(field.value) || field.value.length === 0)
    ) {
      field.onChange(recordCreditos);
      didInit.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordCreditos]);

  const addCredit = () => {
    const newCredit = {
      id: Date.now(),
      banco: '',
      tipoCredito: '',
      capitalInicial: '',
      capitalPendiente: '',
      cuotaMensual: '',
      tae: '',
      porcentajeCierre: '',
      fechaSolicitud: '',
      fechaFinCredito: '',
      contratoTarjeta: '',
      situacion: '',
    };
    field.onChange([...creditos, newCredit]);
  };

  const updateCredit = (id, fieldName, value) => {
    const updatedCreditos = creditos.map(credit =>
      credit.id === id ? { ...credit, [fieldName]: value } : credit
    );
    field.onChange(updatedCreditos);
  };

  const removeCredit = (id) => {
    const updatedCreditos = creditos.filter(credit => credit.id !== id);
    field.onChange(updatedCreditos);
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(parseFloat(value));
  };

  const formatPercentage = (value) => {
    if (!value) return '';
    return `${parseFloat(value).toFixed(2)}%`;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        <CreditCardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Información de Créditos (Vista Administrativa)
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Gestione la información completa de créditos con todos los campos administrativos.
      </Typography>

      {/* Add Credit Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addCredit}
          sx={{ mb: 2 }}
        >
          Agregar Crédito
        </Button>
      </Box>

      {/* Credits Table */}
      {creditos.length > 0 ? (
        <TableContainer component={Paper} sx={{ mb: 3, overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 1500,
              '& th': { fontSize: '0.875rem', py: 1, px: 1.5 },
              '& td': { fontSize: '0.875rem', py: 0.75, px: 1.5 },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: 'var(--ed-soft, #f8f9fa)' }}>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Banco / Financiera</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 130, whiteSpace: 'nowrap' }}>Tipo crédito</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>Capital Inicial</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>Capital pendiente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>Cuota Mensual</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 80, whiteSpace: 'nowrap' }}>TAE</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 80, whiteSpace: 'nowrap' }}>% Cierre</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Fecha Solicitud</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Fecha fin crédito</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>CONTRATO O TARJETA</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Situación</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 80, whiteSpace: 'nowrap' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {creditos.map((credit, index) => (
                <TableRow key={credit.id || index} sx={{ '&:nth-of-type(even)': { backgroundColor: 'var(--ed-soft, #f8f9fa)' } }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TextField
                      variant="standard"
                      size="small"
                      value={credit.banco || ''}
                      onChange={(e) => updateCredit(credit.id, 'banco', e.target.value)}
                      placeholder="Banco"
                      fullWidth
                      sx={{
                        minWidth: 120,
                        '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth variant="standard"
                      sx={{ '& .MuiSelect-select': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                    >
                      <Select
                        value={credit.tipoCredito || ''}
                        onChange={(e) => updateCredit(credit.id, 'tipoCredito', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">Seleccionar</MenuItem>
                        <MenuItem value="Tarjeta de crédito">Tarjeta de crédito</MenuItem>
                        <MenuItem value="Préstamo personal">Préstamo personal</MenuItem>
                        <MenuItem value="Crédito vehicular">Crédito vehicular</MenuItem>
                        <MenuItem value="Hipoteca">Hipoteca</MenuItem>
                        <MenuItem value="Otros">Otros</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <TextField
                      variant="standard"
                      size="small"
                      type="number"
                      value={toNumberInputValue(credit.capitalInicial)}
                      onChange={(e) => updateCredit(credit.id, 'capitalInicial', e.target.value)}
                      placeholder="0.00"
                      inputProps={{ step: "0.01", min: "0", style: { textAlign: 'right' } }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <TextField
                      variant="standard"
                      size="small"
                      type="number"
                      value={toNumberInputValue(credit.capitalPendiente)}
                      onChange={(e) => updateCredit(credit.id, 'capitalPendiente', e.target.value)}
                      placeholder="0.00"
                      inputProps={{ step: "0.01", min: "0", style: { textAlign: 'right' } }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <TextField
                      variant="standard"
                      size="small"
                      type="number"
                      value={toNumberInputValue(credit.cuotaMensual)}
                      onChange={(e) => updateCredit(credit.id, 'cuotaMensual', e.target.value)}
                      placeholder="0.00"
                      inputProps={{ step: "0.01", min: "0", style: { textAlign: 'right' } }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <TextField
                      variant="standard"
                      size="small"
                      type="number"
                      value={toNumberInputValue(credit.tae)}
                      onChange={(e) => updateCredit(credit.id, 'tae', e.target.value)}
                      placeholder="0.00"
                      inputProps={{ step: "0.01", min: "0", max: "100", style: { textAlign: 'center' } }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <TextField
                      variant="standard"
                      size="small"
                      type="number"
                      value={toNumberInputValue(credit.porcentajeCierre)}
                      onChange={(e) => updateCredit(credit.id, 'porcentajeCierre', e.target.value)}
                      placeholder="0.00"
                      inputProps={{ step: "0.01", min: "0", max: "100", style: { textAlign: 'center' } }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      size="small"
                      value={credit.fechaSolicitud || ''}
                      onChange={(e) => updateCredit(credit.id, 'fechaSolicitud', e.target.value)}
                      placeholder="MM/YYYY"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      size="small"
                      value={credit.fechaFinCredito || ''}
                      onChange={(e) => updateCredit(credit.id, 'fechaFinCredito', e.target.value)}
                      placeholder="MM/YYYY"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      size="small"
                      value={credit.contratoTarjeta || ''}
                      onChange={(e) => updateCredit(credit.id, 'contratoTarjeta', e.target.value)}
                      placeholder="NA"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth variant="standard"
                      sx={{ '& .MuiSelect-select': { fontSize: '0.875rem', py: 0, lineHeight: 1.5 } }}
                    >
                      <Select
                        value={normalizeCreditSituation(credit.situacion) || ''}
                        onChange={(e) => updateCredit(credit.id, 'situacion', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">Seleccionar</MenuItem>
                        <MenuItem value="Al corriente de pago">Al corriente de pago</MenuItem>
                        <MenuItem value="Atraso hasta 3 meses">Atraso hasta 3 meses</MenuItem>
                        <MenuItem value="Atraso superior a 3 meses">Atraso superior a 3 meses</MenuItem>
                        <MenuItem value="Impago">Impago</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => removeCredit(credit.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'var(--ed-soft, #f8f9fa)' }}>
          <Typography variant="body1" color="textSecondary">
            No hay créditos registrados. Haga clic en "Agregar Crédito" para comenzar.
          </Typography>
        </Card>
      )}

      {/* Summary Card */}
      {creditos.length > 0 && (
        <Card sx={{ mt: 2, bgcolor: 'var(--ed-info-soft, #e3f2fd)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Resumen de Créditos
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Capital Inicial
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(creditos.reduce((sum, credit) => sum + (parseFloat(credit.capitalInicial) || 0), 0))}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Capital Pendiente
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {formatCurrency(creditos.reduce((sum, credit) => sum + (parseFloat(credit.capitalPendiente) || 0), 0))}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Cuota Mensual
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(creditos.reduce((sum, credit) => sum + (parseFloat(credit.cuotaMensual) || 0), 0))}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Número de Créditos
                </Typography>
                <Typography variant="h6" color="info.main">
                  {creditos.length}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 3, border: '1px solid', borderColor: 'primary.light' }}>
        <CardContent>
          <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalculateIcon /> Calculadora de endeudamiento
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Estimación automática con una TAE del {formatCalculatedPercentage(calculo.tasaAnual)}. Los créditos de la tabla se reúnen en la nueva oferta.
          </Typography>

          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
            General
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Ingresos mensuales</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.ingresos)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Hipoteca / alquiler</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.vivienda)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Otras cuotas fuera de la tabla</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.otrasCuotas)}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
            Situación actual
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Cuotas de los créditos de la tabla</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaCreditos)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Cuota actual total</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaActual)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Ratio actual</Typography>
              <Typography variant="body1" fontWeight={600} sx={{ color: ratioColor(calculo.ratioActual) }}>
                {formatCalculatedPercentage(calculo.ratioActual)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
            Situación nueva
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Honorarios a financiar</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={calculo.honorarios}
                onChange={(e) => updateCalculator('honorarios', e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                placeholder="0,00"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Plazo (meses)</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={calculator.plazoMeses}
                onChange={(e) => updateCalculator('plazoMeses', e.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Capital a reunificar (+1 %)</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.capitalAReunificar)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={!!calculator.incluirSeguro} onChange={(e) => updateCalculator('incluirSeguro', e.target.checked)} />}
                label="Incluir seguro"
              />
              <Typography variant="body2" color="textSecondary">
                Seguro mensual: {formatCalculatedCurrency(calculo.importeSeguro)}. Se calcula sobre el capital con el 1 % y los honorarios incluidos y se suma a la cuota.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Cuota sin seguro</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaSinSeguro)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Cuota con seguro</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaConSeguro)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Total a financiar</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.capitalFinanciado)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Nueva cuota</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaNueva)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Diferencia frente a la cuota de la tabla</Typography>
              <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.diferenciaCuotas)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary">Nuevo ratio de endeudamiento</Typography>
              <Typography variant="body1" fontWeight={600} sx={{ color: ratioColor(calculo.ratioNuevo) }}>
                {formatCalculatedPercentage(calculo.ratioNuevo)}
              </Typography>
            </Grid>
          </Grid>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveOfferSnapshot}
            sx={{ mt: 2 }}
          >
            Guardar oferta
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminEditableCreditInformation;
